import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import {
  GetMyProfileQuery,
  useGetMyProfileQuery,
  useUpdatePreferredLanguageMutation,
} from '__generated__/api-types-and-hooks'
import { normalizeLanguage, STORAGE_KEY, SupportedLanguage } from './index'

type UseLanguageResult = {
  language: SupportedLanguage
  changeLanguage: (lang: SupportedLanguage) => Promise<void>
  isUpdating: boolean
}

/**
 * Reads the user's preferredLanguage from their UserProfile, syncs it to
 * i18next + localStorage, and exposes a setter that persists back to the API.
 * The source of truth is the user record; localStorage is a cache for snappy boot.
 */
export const useLanguage = (): UseLanguageResult => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { data: profile } = useGetMyProfileQuery({}, { staleTime: 60_000 })
  const { mutateAsync, isLoading } = useUpdatePreferredLanguageMutation()

  const remoteLanguage = profile?.getMyProfile?.preferredLanguage
  const current = normalizeLanguage(i18n.language)

  // Tracks the language the user just picked locally so a stale getMyProfile
  // query result (still cached with the previous value during/after the
  // mutation) doesn't override their choice when they navigate between pages.
  const userPickedLangRef = useRef<SupportedLanguage | null>(null)

  // When the API delivers the persisted preference, sync it into i18next —
  // unless the user just picked a language locally, in which case the local
  // choice wins until the cache catches up.
  useEffect(() => {
    if (!remoteLanguage) return
    const next = normalizeLanguage(remoteLanguage)
    if (userPickedLangRef.current && userPickedLangRef.current !== next) return
    if (next === userPickedLangRef.current) userPickedLangRef.current = null
    if (next !== normalizeLanguage(i18n.language)) {
      i18n.changeLanguage(next)
    }
  }, [remoteLanguage, i18n])

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      userPickedLangRef.current = lang
      await i18n.changeLanguage(lang)
      try {
        window.localStorage.setItem(STORAGE_KEY, lang)
      } catch {
        // localStorage may be unavailable (private mode); skip silently.
      }
      // Optimistically update every cached getMyProfile so navigation between
      // pages doesn't read a stale preferredLanguage.
      queryClient.setQueriesData<GetMyProfileQuery | undefined>(
        ['getMyProfile'],
        (prev) =>
          prev?.getMyProfile
            ? { ...prev, getMyProfile: { ...prev.getMyProfile, preferredLanguage: lang } }
            : prev
      )
      try {
        await mutateAsync({ language: lang })
      } finally {
        // Refetch to confirm the server-side value matches our optimistic update.
        queryClient.invalidateQueries(['getMyProfile'])
      }
    },
    [i18n, mutateAsync, queryClient]
  )

  return { language: current, changeLanguage, isUpdating: isLoading }
}
